import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Filters } from "react-table";
import { X as CloseIcon } from "react-feather";

import { fetchEventsById } from "../../store/actions/events.actions";
import { useTypedSelector } from "../../store/models/root.interface";

import { eventsApi, Attendee, FilterGroup, EventFeedback } from "../../api";
import { AttendeeWithFeedback } from "./types/types";

import DownloadCsv from "../../common/components/DownloadCsv";
import DataTable from "../../common/components/ReactTable";
import getColumns from "./EventAttendeesListColumns";
import getCsvColumns from "./EventAttendeesListColumnsCsv";
import TitleMeta from "../../common/components/TitleMeta";
import Loader from "../../common/components/Loader";

import Layout from "../../components/Layout/Layout";
import AttendeeInfoSidebar from "./Sidebar/EventAttendeeInfoSidebar";
import AttendeesSidebar from "./Sidebar/EventAttendeesSidebar";
import { toDictionary } from "../../common/helpers/dictionary";
import { Row, Col } from "reactstrap";

interface Params {
  eventId: string | undefined;
  ticketId: string | undefined;
}

const EventAttendeesList = () => {

  const dispatch = useDispatch();

  const { eventId, ticketId } = useParams<Params>();

  const [items, setItems] = useState<AttendeeWithFeedback[]>([]);

  const [questions, setQuestions] = useState<string[]>([]);
  const [questionIndex, setQuestionIndex] = useState("0");

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const [filters, setFilters] = useState<Filters<AttendeeWithFeedback>>([]);
  const [filteredRecords, setFilteredRecords] = useState<AttendeeWithFeedback[]>();

  const [attendeeId, setAttendeeId] = useState<string>();

  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>();
  const [filterValues, setFilterValues] = useState<string[]>([]);
  const [filterDirection, setFilterDirection] = useState<"ascending" | "descending">("ascending");
  const [filterRakings, setFilterRankings] = useState<string[]>();
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);

  const eventsByUId = useTypedSelector(state => state.event.eventsByUid);

  const eventUid = eventId && ticketId ? `${eventId}-${ticketId}` : void 0;

  const eventName = eventUid ? eventsByUId[eventUid]?.name : void 0;

  const anyQuestions = questions.length !== 0;

  const isFiltered = !!filterRakings;

  const columns = useMemo(
    () => getColumns(isFiltered, anyQuestions, questionIndex),
    [isFiltered, anyQuestions, questionIndex]);

  const csvColumns = useMemo(
    () => getCsvColumns(questions),
    [questions]);

  const records = useMemo<AttendeeWithFeedback[]>(() => {
    let arr = items.slice();
    if (arr.length && filterRakings) {
      if (filterRakings.length) {
        const dict = toDictionary(arr, x => x.id.toString(), x => x);
        arr = filterRakings
          .filter(id => id in dict)
          .map(id => dict[id]);
      }
      else {
        arr = [];
      }
    }
    arr = arr.map(x => {
      if (x.responses) {
        const value = x.responses[questionIndex];
        if (typeof value === "boolean") {
          return { ...x, answer: value ? "Yes" : "No" };
        }
      }
      return { ...x, answer: "" };
    });
    return arr;
  }, [items, filterRakings, questionIndex]);

  const attendee: Attendee = useMemo(() => {
    if (attendeeId) {
      for (let item of items) {
        if (item.id === attendeeId) {
          return item;
        }
      }
    }
    return { id: "", totalEvents: 0 };
  }, [items, attendeeId]);

  const downloadRecords = filteredRecords ?? records;

  const getAttendees = async (eventId: string, ticketId: string): Promise<Attendee[]> => {
    const response = await eventsApi.getAttendeesForEvent({
      eventId,
      ticketId,
    });
    return response.data;
  }

  const getFeedback = async (eventId: string, ticketId: string): Promise<EventFeedback> => {
    try {
      const response = await eventsApi.getFeedbackForEvent({
        eventId,
        ticketId,
      });
      return response.data;
    }
    catch {
      return {
        questions: [],
        responses: {},
      };
    }
  }

  const augmentAttendees: (items: Attendee[], responses: Record<string, Record<string, boolean>>) => AttendeeWithFeedback[] = (items, responses) => items.map(x => {
    const obj = responses[x.id];
    return { ...x, answer: "", ...(obj ? { responses: obj } : {}) };
  });

  const getDataAsync = async (eventId: string, ticketId: string) => {
    setIsLoading(true);
    setIsError(false);
    Promise.all([
      getAttendees(eventId, ticketId),
      getFeedback(eventId, ticketId),
    ])
      .then(([attendees, feedback]) => {
        setItems(augmentAttendees(attendees, feedback.responses));
        setQuestions(feedback.questions);
        setQuestionIndex("0");
        setIsLoading(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      })
  };

  const getFiltersAsync = async () => {
    setIsLoadingFilters(true);
    try {
      const response = await eventsApi.getAttendeeFilters();
      setFilterGroups(response.data);
      setIsLoadingFilters(false);
    }
    catch {
      setFilterGroups([]);
      setIsLoadingFilters(false);
    }
  };

  const getRankingsAsync = async (eventId: string, ticketId: string, facets: string[], direction: "ascending" | "descending") => {
    setIsFiltering(true);
    try {
      const response = await eventsApi.getAttendeeRankings({
        eventId,
        ticketId,
        facets,
        direction,
      });
      setFilterRankings(response.data);
      setIsFiltering(false);
    }
    catch {
      setIsFiltering(false);
    }
  };

  // Fetch event and attendee info
  useEffect(() => {
    if (eventId && ticketId) {
      dispatch(fetchEventsById([eventId]));
      getDataAsync(eventId, ticketId);
    }
  }, [eventId, ticketId]);

  // Fetch filters
  useEffect(() => {
    if (!filterGroups) {
      getFiltersAsync();
    }
  }, [filterGroups]);

  // Reset filtered records
  useEffect(() => {
    setFilteredRecords(void 0);
  }, [eventId, ticketId]);

  // Always enter with no attendee selected
  useEffect(() => {
    setAttendeeId(void 0);
  }, [eventId, ticketId]);

  useEffect(() => {
    const facets = filterValues.slice();
    const direction = filterDirection;
    const timeoutId = window.setTimeout(() => {
      if (eventId && ticketId && facets.length) {
        getRankingsAsync(eventId, ticketId, facets, direction);
      }
      else {
        setFilterRankings(void 0);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filterValues, filterDirection]);

  const handleFilters = (filters: Filters<AttendeeWithFeedback>, results: AttendeeWithFeedback[]) => {
    setFilters(filters);
    setFilteredRecords(results);
  };

  const handleSelect = (attendee: Attendee) => {
    if (attendee.id !== attendeeId) {
      setAttendeeId(attendee.id);
    }
    else {
      setAttendeeId(void 0);
    }
  };

  const handleFilterChange = (name: string, value: string, checked: boolean) => {
    if (checked) {
      setFilterValues(filterValues.concat([value]));
    }
    else {
      const values = filterValues.slice();
      const index = values.indexOf(value);
      if (index !== -1) {
        values.splice(index, 1);
        setFilterValues(values);
      }
    }
  };

  const handleFilterDirectionChange = (direction: "ascending" | "descending") => {
    setFilterDirection(direction);
  };

  const handleFilterReset = () => {
    setFilterDirection("ascending");
    setFilterValues([]);
  };

  return (
    <>
      <TitleMeta title={`Attendees - ${eventName}`} />

      <Layout.SidebarPage>

        <Layout.Main>

          <Layout.Header>
            <Layout.Breadcrumb items={[["Events", "/events"]]} />
            <Row>
              {/* Left */}
              <Col xs={12} lg={6}>
                {/* Title */}
                <Layout.Title>Attendees</Layout.Title>
                {/* Event name */}
                <div className="h5 mb-0 text-neutral">{eventName ?? (<>&nbsp;</>)}</div>
              </Col>
              {/* Right */}
              <Col xs={12} lg={6}>
                <div className="d-flex align-items-center justify-content-end mt-2 mt-lg-0">
                  {/* Questions */}
                  {anyQuestions && (
                    <div>
                      <label htmlFor="EventQuestion" className="sr-only">Question</label>
                      <select id="EventQuestion" className="form-control" value={questionIndex} onChange={(e) => setQuestionIndex(e.target.value)}>
                        {Object.entries(questions).map(([key, text]) => (
                          <option key={key} value={key}>{text}</option>))}
                      </select>
                    </div>
                  )}
                  {/* Download */}
                  <div className="ml-4">
                    <DownloadCsv
                      filename={`Attendees - ${eventName}`}
                      columns={csvColumns}
                      data={downloadRecords}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </Layout.Header>

          <Layout.Body>
            {isError ? (
              <p className="py-2 text-center">Sorry, something went wrong.</p>
            ) : (
              <div className="position-relative">
                <DataTable
                  columns={columns}
                  data={records}
                  filters={filters}
                  selected={attendee => attendee.id === attendeeId}
                  onSelect={handleSelect}
                  onChangeFilter={handleFilters}
                  isLoading={isLoading}
                  messages={{
                    noRecords: isFiltered ? "No matching attendees found" : "No attendees found",
                    noMatches: "No matching attendees found",
                  }}
                />
                <Loader show={!isLoading && isFiltering} background="panel-light" />
              </div>
            )}
          </Layout.Body>

        </Layout.Main>

        <Layout.Sidebar>
          {attendeeId
            ? (
              <div className="position-relative">
                <AttendeeInfoSidebar {...attendee} />
                <CloseIcon
                  className="sidebarWrapper__icon nav-icon mt-2 mr-2"
                  onClick={() => { setAttendeeId(void 0); }}
                />
              </div>
            )
            : (
              <AttendeesSidebar
                items={filterGroups ?? []}
                values={filterValues}
                sortDirection={filterDirection}
                totalAttendees={items.length}
                isLoadingData={isLoading}
                isLoadingFilters={isLoadingFilters}
                onChange={handleFilterChange}
                onSortChanged={handleFilterDirectionChange}
                onReset={handleFilterReset}
              />
            )}
        </Layout.Sidebar>

      </Layout.SidebarPage>
    </>
  );
};

export default EventAttendeesList;
